import type { FC } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import featuredItemPlaceholder from 'assets/images/Afb5.png';
import type ApiFeaturedItem from 'src/core/models/api/ApiFeaturedItem';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Translations from 'src/core/utils/Translations';
import { useFeaturedItemStore } from 'src/domain/stores/useFeaturedItemStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import BottomSheet from 'src/presentation/components/organisms/BottomSheet';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import LeaveAppDialog from 'src/presentation/dialogs/LeaveAppDialog';

interface SheetProps {
	item: ApiFeaturedItem;
	onClose: () => void;
}

interface Action {
	label: string;
	url: string | null;
	isReservation: boolean;
}

const metaLine = (item: ApiFeaturedItem): string | null =>
	item.featured_item_type === 'PRODUCT' ? item.workspace_name : item.subtitle;

const priceLine = (item: ApiFeaturedItem): string | null => {
	if (item.price === null) {
		return null;
	}

	const suffix = item.featured_item_type === 'PRODUCT' ? Translations.perPersonShort : item.price_suffix;

	return `${item.price} ${suffix ?? Translations.perPersonShort}`;
};

// A product always offers its reservation flow, an editorial item only the link the dmp filled in.
const actionFor = (item: ApiFeaturedItem): Action | null => {
	if (item.featured_item_type === 'PRODUCT') {
		return { label: Translations.bookNow, url: item.reservation_flow_url, isReservation: true };
	}

	if (item.button_link === null || item.button_text === null) {
		return null;
	}

	return { label: item.button_text, url: item.button_link, isReservation: false };
};

const FeaturedItemSheet: FC<SheetProps> = ({ item, onClose }: SheetProps) => {
	const { confirmLeaving } = useLeaveAppStore();

	const meta = metaLine(item);
	const price = priceLine(item);
	const action = actionFor(item);
	const usps = item.featured_item_type === 'PRODUCT' ? item.usps : [];

	// The leave-app dialog opens over the sheet, which has to stay put underneath it.
	const openUrl = (url: string, isReservation: boolean) =>
		confirmLeaving(
			isReservation
				? {
						url,
						description: Translations.reserveLeaveAppDescription,
						webMessage: Constants.bridgeMessageReservationFlowOpen,
					}
				: { url },
		);

	return (
		<BottomSheet visible={true} onClose={onClose}>
			<View style={styles.media}>
				<ThermenImage
					contentFit="cover"
					source={item.image ? { uri: item.image } : featuredItemPlaceholder}
					style={styles.poster}
				/>
			</View>

			<View style={styles.body}>
				{meta === null ? null : <Text style={styles.meta}>{meta}</Text>}
				<Text style={styles.title}>{item.name}</Text>
				{item.description === null ? null : <HtmlView html={item.description} />}

				{usps.map((usp, index) =>
					usp.description === null ? null : <HtmlView html={`<li> • ${usp.description}</li>`} key={index} />,
				)}

				{price === null ? null : <Text style={styles.price}>{price}</Text>}
			</View>

			{action === null ? null : (
				<PrimaryButton
					disabled={action.url === null}
					label={action.label}
					onPress={() => (action.url === null ? undefined : openUrl(action.url, action.isReservation))}
				/>
			)}

			{/* iOS refuses a second modal over a modal, so the confirmation lives in this sheet's own tree. */}
			<LeaveAppDialog />
		</BottomSheet>
	);
};

const FeaturedItemDialog: FC = () => {
	const { selectedItem, close } = useFeaturedItemStore();

	if (selectedItem === null) {
		return null;
	}

	return <FeaturedItemSheet item={selectedItem} onClose={close} />;
};

const styles = StyleSheet.create({
	media: {
		aspectRatio: Dimens.aspectRatio16By9,
		borderRadius: Dimens.borderRadius4,
		backgroundColor: Colors.gray100,
		overflow: 'hidden',
	},
	poster: {
		width: '100%',
		height: '100%',
	},
	body: {
		gap: Dimens.gap4,
	},
	meta: {
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight21,
		color: Colors.gray650,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize22,
		lineHeight: Dimens.lineHeight30,
		color: Colors.gray900,
	},
	price: {
		fontFamily: Fonts.barlow,
		fontSize: Dimens.fontSize18,
		fontWeight: Dimens.fontWeight700,
		color: Colors.gray900,
		marginTop: Dimens.gap10,
	},
});

export default FeaturedItemDialog;
